import React, { useEffect, useState } from 'react';
import { Plus, List, Settings } from 'react-feather';
import { twMerge } from 'tailwind-merge';

import { DillDispute } from '@dill/dill-shared';
import { useAppSelector, useAppDispatch } from '../../../../redux/store';
import { getSupplierDisputes, updateDispute } from '../../../../redux/services/disputesService';
import { DillButton } from '../../../general/Dill';
import SupplierDisputesTable from './SupplierDisputesTable';
import { DisputeDetailsModal } from './DisputeDetailsModal';
import { CreateDisputeModal } from './CreateDisputeModal';
import { DisputeRoutingRulesTab } from './DisputeRoutingRulesTab';

function SupplierDisputes({ buyerId }: { buyerId?: string }) {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { disputes } = useAppSelector((state) => state.disputes);

  const [activeTab, setActiveTab] = useState<'DISPUTES' | 'MANAGEMENT'>('DISPUTES');
  const [selectedDispute, setSelectedDispute] = useState<DillDispute | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierDisputes({ supplierId: currentSupplier.id, buyerId }));
    }
  }, [currentSupplier?.id, buyerId, dispatch]);

  const handleMarkResolved = async (disputeId: string) => {
    if (currentSupplier?.id) {
      await dispatch(
        updateDispute({ supplierId: currentSupplier.id, disputeId, isResolved: true })
      );
      dispatch(getSupplierDisputes({ supplierId: currentSupplier.id, buyerId }));
    }
  };

  const handleRowClick = (dispute: DillDispute) => {
    setSelectedDispute(dispute);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDispute(null);
  };

  const handleDisputeCreated = () => {
    if (currentSupplier?.id) {
      dispatch(getSupplierDisputes({ supplierId: currentSupplier.id, buyerId }));
    }
  };

  const tabs = [
    { id: 'DISPUTES' as const, label: 'Disputes', icon: <List size={14} /> },
    { id: 'MANAGEMENT' as const, label: 'Dispute Management', icon: <Settings size={14} /> },
  ];

  const height =
    process.env.REACT_APP_ENVIRONMENT !== 'production' ? 'calc(100vh-140px)' : 'calc(100vh-80px)';
  return (
    <div className="w-full h-full p-4">
      {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1 p-1 rounded-xl bg-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={twMerge(
                  'flex-1 flex items-center justify-center gap-1.5 h-[34px] w-[200px] text-xs font-medium rounded-lg border-none cursor-pointer transition-all px-3',
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'bg-transparent text-gray-500 hover:text-gray-800'
                )}>
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'DISPUTES' ? (
        <div className={`w-full h-[${height}]`}>
          <SupplierDisputesTable
            disputes={disputes}
            onMarkResolved={handleMarkResolved}
            onRowClick={handleRowClick}
            toolbarRight={
              <DillButton
                label="New Dispute"
                icon={<Plus size={14} />}
                onClick={() => setIsCreateModalOpen(true)}
                height={31}
              />
            }
          />
        </div>
      ) : (
        currentSupplier?.id && <DisputeRoutingRulesTab supplierId={currentSupplier.id} />
      )}

      <DisputeDetailsModal
        open={isModalOpen}
        dispute={selectedDispute}
        onClose={handleCloseModal}
        onMarkResolved={handleMarkResolved}
      />

      <CreateDisputeModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        buyerId={buyerId}
        onCreated={handleDisputeCreated}
      />
    </div>
  );
}

export default SupplierDisputes;
